import { Injectable, type Signal, computed, inject, signal, untracked } from '@angular/core';

import { LoggerService } from '@gde-mit/angular-core';

import { CoverGenerationQueue } from '@features/kt/activity-browser/services/cover-generation.queue';

import type { CoverResult, CoverRunOutcome } from '@shared/models/cover-generation-queue.port';

import type { ActivityEditDraft, ActivityEditFlushResult } from '../models/activity-edit-field.model';

/** How a manual generation ended: the queue's typed outcome, or `saveFailed` when the pending edits did not save. */
export type ActivityCoverGenerateOutcome = 'saveFailed' | CoverRunOutcome;

/** A manual generation that ended, and how. */
export interface ActivityCoverRunEnd {
  readonly activityId: string;
  readonly outcome: ActivityCoverGenerateOutcome;
}

/**
 * What the cover store needs to know about the activity the editor shows: its id, the cover as loaded and the saved
 * text the leave rule judges.
 */
export type ActivityCoverSeed = Pick<ActivityEditDraft, 'cover' | 'coverGeneration' | 'description' | 'id' | 'name'>;

interface ManualRun {
  readonly activityId: string;
  readonly controller: AbortController;
}

type SaveBeforeGeneration = () => Promise<ActivityEditFlushResult>;

const NO_SEED: Signal<ActivityCoverSeed | null> = signal<ActivityCoverSeed | null>(null).asReadonly();
const NO_COVER: CoverResult = { cover: null, coverGeneration: null };

/**
 * The activity editor's handle on the cover: what to show, whether a generation is running, and the one manual
 * generation behind Generate, Retry and Regenerate.
 *
 * Provided on the edit route next to the sticker store, so it outlives the page: the page must call {@link reset}
 * when it is destroyed. It holds no cover state of its own; {@link view} is the cover generation queue's answer for
 * the activity, with the seed as the fallback. It does not inject the edit store: the page hands over the seed with
 * {@link setSeed} and the save step with {@link setPrepare}, so it works under any injector.
 */
// eslint-disable-next-line @angular-eslint/use-injectable-provided-in -- route-scoped store, provided via route `providers`
@Injectable()
export class ActivityCoverStore {
  readonly #queue = inject(CoverGenerationQueue);
  readonly #logger = inject(LoggerService);

  readonly #seed = signal<Signal<ActivityCoverSeed | null>>(NO_SEED);
  readonly #manualActivityId = signal<string | null>(null);
  readonly #outcome = signal<ActivityCoverRunEnd | null>(null);

  // Split so a draft change that leaves the cover alone does not recompute `view`.
  readonly #activityId = computed(() => this.#seed()()?.id ?? null);
  readonly #seedCover = computed(() => this.#seed()()?.cover ?? null);
  readonly #seedGeneration = computed(() => this.#seed()()?.coverGeneration ?? null);
  readonly #seedResult = computed<CoverResult>(() => ({
    cover: this.#seedCover(),
    coverGeneration: this.#seedGeneration(),
  }));
  /** The name the editor's activity currently has; empty while there is none. */
  readonly activityName = computed(() => this.#seed()()?.name ?? '');
  /** The cover and generation record to show for the editor's activity. */
  readonly view = computed<CoverResult>(() => {
    const activityId = this.#activityId();

    return activityId === null ? NO_COVER : this.#queue.coverOf(activityId, this.#seedResult());
  });
  /** True from the Generate click, through the pending save, to the answer, while the editor shows that activity. */
  readonly manualActive = computed(() => {
    const manualActivityId = this.#manualActivityId();

    return manualActivityId !== null && manualActivityId === this.#activityId();
  });
  /** True while a manual or any other generation for the editor's activity is running. */
  readonly generating = computed(() => {
    if (this.manualActive()) {
      return true;
    }

    const activityId = this.#activityId();

    return activityId !== null && this.#queue.inFlight(activityId);
  });
  /**
   * The last manual generation for the editor's activity that ended; `null` before one ends or once a new one starts.
   * Each run that ends is a new value, so an effect on it runs again when two runs in a row end the same way, even
   * when it never saw the `null` between them; an effect on {@link lastOutcome} would not.
   */
  readonly lastRun = computed<ActivityCoverRunEnd | null>(() => {
    const last = this.#outcome();

    return last !== null && last.activityId === this.#activityId() ? last : null;
  });
  /** How the last manual generation for the editor's activity ended; `null` before one ends or once a new one starts. */
  readonly lastOutcome = computed<ActivityCoverGenerateOutcome | null>(() => this.lastRun()?.outcome ?? null);

  #prepare: SaveBeforeGeneration | null = null;
  #run: ManualRun | null = null;
  /** Manual run the leave guard already cancelled, so {@link leave} must not enqueue another. */
  #dismissedManualId: string | null = null;

  /**
   * Registers where the editor's activity and loaded cover come from. Cleared by {@link reset}.
   *
   * @param seed The edit store's draft, or any signal of the activity's id and cover as loaded.
   */
  setSeed(seed: Signal<ActivityCoverSeed | null>): void {
    this.#seed.set(seed);
  }

  /**
   * Registers how pending edits are saved before a generation. Cleared by {@link reset}.
   *
   * @param prepare Saves the pending edits; resolves to what that flush found.
   */
  setPrepare(prepare: SaveBeforeGeneration): void {
    this.#prepare = prepare;
  }

  /**
   * Generates a cover for the editor's activity: saves the pending edits first and sends nothing when that fails.
   * Does nothing while a generation for the activity is running, from any surface, or until {@link setSeed} and
   * {@link setPrepare} were called. The outcome lands in {@link lastRun} and {@link lastOutcome}.
   */
  async generate(): Promise<void> {
    const prepare = this.#prepare;
    const activityId = untracked(() => this.#activityId());

    if (untracked(() => this.generating()) || prepare === null || activityId === null) {
      return;
    }

    this.#abortRun();
    this.#dismissedManualId = null;

    const run: ManualRun = { activityId, controller: new AbortController() };
    this.#run = run;
    this.#manualActivityId.set(activityId);
    this.#outcome.set(null);

    try {
      await this.#runGeneration(run, prepare);
    } finally {
      if (this.#run === run) {
        this.#run = null;
        this.#manualActivityId.set(null);
      }
    }
  }

  /**
   * Cancels the manual generation that is running, if any: a pending save is left to finish but no request follows
   * it, and a request in flight is cancelled. Shows no outcome. {@link manualActive} ends at once; {@link generating}
   * ends when the queue has settled the cancelled call.
   */
  abort(): void {
    this.#abortRun();
  }

  /**
   * Cancels the manual generation because the teacher confirmed leaving.
   * {@link leave} for the same activity then skips the automatic enqueue, so one click is not billed twice.
   */
  dismissManualForLeave(): void {
    const activityId = this.#run?.activityId ?? untracked(() => this.#manualActivityId());

    this.#abortRun();

    if (activityId !== null) {
      this.#dismissedManualId = activityId;
    }
  }

  /**
   * Ends the editor's visit to an activity, once the navigation away has completed.
   *
   * With a manual generation pending or running for the activity, that generation is cancelled and nothing else
   * happens: leaving is the teacher's cancel, and one click never bills twice. Without one, the activity goes to the
   * queue's automatic entry, which starts a generation only when it is still eligible. Either way the outcome of the
   * visit is forgotten, so a later visit starts without the old message.
   *
   * The leave rule needs the activity's saved text, so it runs only while the seed still shows the activity: the
   * caller leaves before the editor loads another one.
   *
   * @param activityId The activity the editor is leaving.
   */
  leave(activityId: string): void {
    const wasManual = this.#run?.activityId === activityId || this.#dismissedManualId === activityId;

    if (wasManual) {
      this.#abortRun();
    }

    if (this.#dismissedManualId === activityId) {
      this.#dismissedManualId = null;
    }

    if (untracked(() => this.#outcome())?.activityId === activityId) {
      this.#outcome.set(null);
    }

    if (!wasManual) {
      this.#queueLeftActivity(activityId);
    }
  }

  /** Cancels any manual generation and forgets the seed, the registered save step and the outcome. */
  reset(): void {
    this.#abortRun();
    this.#dismissedManualId = null;
    this.#prepare = null;
    this.#seed.set(NO_SEED);
    this.#outcome.set(null);
  }

  #queueLeftActivity(activityId: string): void {
    const seed = untracked(() => this.#seed()());

    if (seed?.id !== activityId) {
      return;
    }

    this.#queue.enqueue({
      activityId,
      isOwner: true,
      cover: seed.cover,
      coverGeneration: seed.coverGeneration,
      savedText: { name: seed.name, description: seed.description },
    });
  }

  async #runGeneration(run: ManualRun, prepare: SaveBeforeGeneration): Promise<void> {
    const flushResult = await this.#flushPendingEdits(prepare);

    if (!this.#isCurrent(run)) {
      return;
    }

    if (flushResult !== 'ok') {
      if (flushResult === 'failed') {
        this.#outcome.set({ activityId: run.activityId, outcome: 'saveFailed' });
      }

      return;
    }

    const outcome = await this.#queue.runNow(
      run.activityId,
      run.controller.signal,
      untracked(() => this.#seedResult()),
    );

    if (this.#isCurrent(run)) {
      this.#outcome.set({ activityId: run.activityId, outcome });
    }
  }

  async #flushPendingEdits(prepare: SaveBeforeGeneration): Promise<ActivityEditFlushResult> {
    try {
      return await prepare();
    } catch (error) {
      this.#logger.warn('Activity cover: saving the pending edits failed', error);

      return 'failed';
    }
  }

  // A run stops being current when it is aborted or the editor shows another activity; its answer then lands in the
  // queue under its own id and is shown to no one.
  #isCurrent(run: ManualRun): boolean {
    return this.#run === run && untracked(() => this.#activityId()) === run.activityId;
  }

  #abortRun(): void {
    const run = this.#run;

    if (run === null) {
      return;
    }

    this.#run = null;
    this.#manualActivityId.set(null);
    run.controller.abort();
  }
}
